import { Injectable, signal } from "@angular/core";

export interface RouteStop {
  code: string;
  name: string;
  /** Position in the real Sri Lanka district-map coordinate space (see sri-lanka-geo.ts SL_VIEWBOX) */
  x: number;
  y: number;
}

export interface ItineraryDay {
  day: string;
  stopCode: string;
  title: string;
  activities: string[];
}

export interface Tour {
  id: string;
  title: string;
  tagline: string;
  location: string;
  region: string;
  province: string;
  price: number;
  priceNote: string;
  rating: number;
  reviews: number;
  duration: string;
  durationDays: number;
  persons: string;
  difficulty: "Easy" | "Moderate" | "Active";
  bestSeason: string;
  destinations: string;
  image: string;
  gallery: string[];
  description: string;
  highlights: string[];
  route: string[];
  stops: RouteStop[];
  itinerary: ItineraryDay[];
  departureTime?: string;
  returnTime?: string;
  included: string[];
  excluded: string[];
}

/**
 * Real coordinates in the sri-lanka-geo.ts district-map space (449.68 x 792.55),
 * anchored to actual district centroids computed from the geo data, with small
 * manual offsets for towns that sit inside a larger district (e.g. Sigiriya
 * within Matale, Ella within Badulla).
 */
const STOPS: Record<string, RouteStop> = {
  CMB: { code: "CMB", name: "Colombo", x: 77, y: 605 },
  NEG: { code: "NEG", name: "Negombo", x: 52, y: 528 },
  BEN: { code: "BEN", name: "Bentota", x: 78, y: 693 },
  GAL: { code: "GAL", name: "Galle", x: 120, y: 735 },
  MIR: { code: "MIR", name: "Mirissa", x: 145, y: 750 },
  KDY: { code: "KDY", name: "Kandy", x: 216, y: 531 },
  NUE: { code: "NUE", name: "Nuwara Eliya", x: 212, y: 572 },
  ELL: { code: "ELL", name: "Ella", x: 250, y: 595 },
  SIG: { code: "SIG", name: "Sigiriya", x: 232, y: 372 },
  DAM: { code: "DAM", name: "Dambulla", x: 215, y: 390 },
  ANU: { code: "ANU", name: "Anuradhapura", x: 166, y: 298 },
  YAL: { code: "YAL", name: "Yala", x: 350, y: 670 },
  PIN: { code: "PIN", name: "Pinnawela", x: 150, y: 575 },
  POL: { code: "POL", name: "Polonnaruwa", x: 290, y: 365 },
  HIK: { code: "HIK", name: "Hikkaduwa", x: 108, y: 723 },
  BAN: { code: "BAN", name: "Bandarawela", x: 238, y: 603 },
  KAT: { code: "KAT", name: "Kataragama", x: 334, y: 660 },
  HAB: { code: "HAB", name: "Habarana", x: 230, y: 351 },
};

@Injectable({
  providedIn: "root",
})
export class DataService {
  private tours = signal<Tour[]>([
    {
      id: "colombo-heritage-trail",
      title: "Colombo Heritage Trail",
      tagline: "The capital in a single, well-paced day",
      location: "Colombo",
      region: "West Coast",
      province: "Western Province",
      price: 89,
      priceNote: "private guide & vehicle included",
      rating: 4.7,
      reviews: 94,
      duration: "1 Day",
      durationDays: 1,
      persons: "2 Persons",
      difficulty: "Easy",
      bestSeason: "Year-round",
      destinations: "4 Landmarks",
      image: "./home-grid-2.png",
      gallery: ["./home-grid-2.png"],
      description:
        "In 1803 the colonial administrator Robert Percival wrote that Colombo had \"no part of the world where so many different languages are spoken, or which contains such a mixture of nations, manners, and religions.\" Little has changed - the gilded Gangaramaya Temple, the lakeside Seema Malaka shrine, the colonial-era Fort district, and the bronze Buddha statues of Beira Lake still sit within the same low, humid skyline that has watched over the harbour for a century.",
      highlights: [
        "Beira Lake and the Seema Malaka meditation pavilion",
        "Gangaramaya Temple’s eclectic architecture",
        "Dutch and British colonial Fort district on foot",
        "Pettah's spice and textile bazaar streets",
      ],
      route: ["CMB"],
      stops: [STOPS["CMB"]],
      itinerary: [
        {
          day: "Day 1",
          stopCode: "CMB",
          title: "The Capital, on Foot",
          activities: [
            "Morning pickup from your Colombo hotel or the airport",
            "Gangaramaya Temple and the bronze Buddhas of Beira Lake",
            "Colonial Fort district and the old Dutch Hospital precinct",
            "Pettah bazaar - spice stalls, textiles, and street food",
            "Evening drop-off, or transfer onward to your next stop",
          ],
        },
      ],
      departureTime: "08:30 AM",
      returnTime: "05:30 PM",
      included: [
        "Private air-conditioned vehicle",
        "English-speaking guide",
        "Bottled water",
      ],
      excluded: ["Meals", "Entrance fees", "Personal expenses", "Accommodation"],
    },
    {
      id: "sigiriya-ancient-rock",
      title: "Sigiriya Ancient Rock",
      tagline: "The fifth-century sky palace, at sunrise",
      location: "Sigiriya",
      region: "Cultural Triangle",
      province: "Central Province",
      price: 340,
      priceNote: "private guide & vehicle included",
      rating: 4.9,
      reviews: 231,
      duration: "2 Days",
      durationDays: 2,
      persons: "2 Persons",
      difficulty: "Moderate",
      bestSeason: "Year-round",
      destinations: "3 Destinations",
      image: "./tour-sigiriya-1.png",
      gallery: ["./tour-sigiriya-1.png", "./tour-pinnawela-1.jpg", "./home-grid-1.png"],
      description:
        "Climb the 200-metre column of rock that King Kashyapa turned into a fortress-palace in the 5th century - past the Lion's Paw gateway, the mirror wall still faintly graffitied by ninth-century visitors, and the cloud-line frescoes - with a stop among Pinnawela's elephants and Dambulla's candlelit cave temples on the way in.",
      highlights: [
        "Bathing hour at the Pinnawela Elephant Orphanage en route",
        "Dambulla Cave Temple's five painted shrines",
        "Sunrise ascent of Sigiriya Rock Fortress, a UNESCO World Heritage Site",
        "Optional jeep safari at Minneriya or Kaudulla National Park",
      ],
      route: ["CMB", "DAM", "SIG", "CMB"],
      stops: [STOPS["CMB"], STOPS["DAM"], STOPS["SIG"]],
      itinerary: [
        {
          day: "Day 1",
          stopCode: "DAM",
          title: "Elephants & Cave Temples",
          activities: [
            "Early pickup from Bandaranaike International Airport, transfer toward the Cultural Triangle",
            "Stop at the Pinnawela Elephant Orphanage for the riverside bathing hour",
            "Afternoon visit to the Dambulla Cave Temple",
            "Overnight near Sigiriya",
          ],
        },
        {
          day: "Day 2",
          stopCode: "SIG",
          title: "The Fortress Climb",
          activities: [
            "Sunrise climb to the summit of Sigiriya Rock",
            "Explore the ruins of the royal palace and water gardens",
            "Optional afternoon safari jeep at Minneriya National Park",
            "Transfer back to Bandaranaike International Airport for departure",
          ],
        },
      ],
      departureTime: "05:00 AM",
      returnTime: "07:00 PM",
      included: [
        "Private air-conditioned vehicle",
        "English-speaking guide",
        "1 night 3-star hotel",
        "Pinnawela Elephant Orphanage entry",
      ],
      excluded: [
        "International flights",
        "Entrance fees",
        "Optional Minneriya safari jeep",
        "Travel insurance",
        "Personal expenses",
      ],
    },
    {
      id: "kandy-cultural-heritage",
      title: "Kandy Cultural Heritage",
      tagline: "The last Kandyan kingdom, still standing",
      location: "Kandy",
      region: "Hill Country",
      province: "Central Province",
      price: 745,
      priceNote: "private guide & vehicle included",
      rating: 4.9,
      reviews: 156,
      duration: "5 Days",
      durationDays: 5,
      persons: "2 Persons",
      difficulty: "Easy",
      bestSeason: "Year-round",
      destinations: "3 Destinations",
      image: "./tour-kandy-1.png",
      gallery: ["./tour-kandy-1.png", "./home-grid-3.png"],
      description:
        "Kandy held out against colonial rule longer than any other Sri Lankan kingdom, and it shows - in the sacred Temple of the Tooth Relic lit gold against the lake at night, in the Kandyan dance drummed out in the same rhythms for centuries, and in the Royal Botanical Gardens' avenue of century-old palms. From there the route climbs into the cool tea country around Nuwara Eliya before looping back down to a day in the capital.",
      highlights: [
        "Temple of the Sacred Tooth Relic, illuminated after dusk",
        "Royal Botanical Gardens, Peradeniya",
        "Traditional Kandyan dance and drumming performance",
        "Nuwara Eliya's tea terraces and Ramboda Falls",
        "A Colombo city day - Gangaramaya Temple, Fort and Pettah",
      ],
      route: ["CMB", "KDY", "NUE", "CMB"],
      stops: [STOPS["CMB"], STOPS["KDY"], STOPS["NUE"]],
      itinerary: [
        {
          day: "Day 1",
          stopCode: "KDY",
          title: "Airport to Kandy",
          activities: [
            "Pick up from Bandaranaike International Airport",
            "Transfer to Kandy and check in to your hotel",
            "Evening visit to the Temple of the Sacred Tooth Relic",
            "Traditional Kandyan dance performance",
          ],
        },
        {
          day: "Day 2",
          stopCode: "NUE",
          title: "Kandy to Nuwara Eliya",
          activities: [
            "Morning at the Royal Botanical Gardens, Peradeniya",
            "Visit a working tea factory and plantation",
            "Transfer to Nuwara Eliya via Ramboda Falls",
            "Evening at leisure in the hill station once called \"Little England\"",
          ],
        },
        {
          day: "Day 3",
          stopCode: "CMB",
          title: "Nuwara Eliya to Colombo",
          activities: [
            "Morning walk through Victoria Park and the tea terraces",
            "Scenic transfer back down to Colombo",
            "Evening at leisure in the capital",
          ],
        },
        {
          day: "Day 4",
          stopCode: "CMB",
          title: "Colombo",
          activities: [
            "Gangaramaya Temple and the bronze Buddhas of Beira Lake",
            "Colonial Fort district on foot",
            "Pettah bazaar - spice stalls, textiles and street food",
          ],
        },
        {
          day: "Day 5",
          stopCode: "CMB",
          title: "Departure",
          activities: [
            "Transfer to Bandaranaike International Airport for departure",
            "Or extend the trip with a transfer onward to a beach stay",
          ],
        },
      ],
      departureTime: "08:00 AM",
      returnTime: "06:00 PM",
      included: [
        "Private air-conditioned vehicle",
        "English-speaking guide",
        "4 nights hotel",
        "Daily breakfast",
      ],
      excluded: [
        "Entrance fees",
        "Lunch & dinner",
        "Travel insurance",
        "Personal expenses",
      ],
    },
    {
      id: "panoramic-tours-sri-lanka",
      title: "Panoramic Tours in Sri Lanka",
      tagline: "The full island, coast to summit to coast",
      location: "Island-wide",
      region: "Full Circuit",
      province: "Multi-Province",
      price: 1690,
      priceNote: "private guide & vehicle included",
      rating: 5.0,
      reviews: 87,
      duration: "9 Days",
      durationDays: 9,
      persons: "2 Persons",
      difficulty: "Moderate",
      bestSeason: "Year-round",
      destinations: "8 Destinations",
      image: "./home-grid-4.png",
      gallery: [
        "./home-grid-4.png",
        "./home-grid-2.png",
        "./tour-sigiriya-1.png",
        "./home-grid-3.png",
        "./home-grid-5.png",
        "./tour-bentota-1.jpg",
      ],
      description:
        "Our flagship circuit, and the one itinerary that actually earns the name - nine days from Colombo's colonial streets through the Cultural Triangle's ancient capitals, up into Kandy and the tea highlands, out to a Yala safari at dawn, and down to a Bentota finale of river safaris and Ayurveda. Everything this island does well, in one continuous route.",
      highlights: [
        "Sigiriya Rock Fortress and Dambulla’s cave temples",
        "Temple of the Sacred Tooth Relic in Kandy",
        "World's End, on foot, at Horton Plains",
        "Dawn leopard safari at Yala National Park",
        "A Bentota finale - the Madu River and an Ayurveda treatment",
      ],
      route: ["CMB", "SIG", "DAM", "KDY", "NUE", "ELL", "YAL", "BEN", "CMB"],
      stops: [
        STOPS["CMB"],
        STOPS["SIG"],
        STOPS["DAM"],
        STOPS["KDY"],
        STOPS["NUE"],
        STOPS["ELL"],
        STOPS["YAL"],
        STOPS["BEN"],
      ],
      itinerary: [
        {
          day: "Day 1",
          stopCode: "CMB",
          title: "Arrival in Colombo",
          activities: [
            "Airport pickup and transfer to your Colombo hotel",
            "Afternoon Colombo Heritage Trail - Gangaramaya, Fort, Pettah",
            "Welcome dinner briefing on the days ahead",
          ],
        },
        {
          day: "Day 2",
          stopCode: "SIG",
          title: "Into the Cultural Triangle",
          activities: [
            "Morning transfer to Sigiriya",
            "Afternoon climb of Sigiriya Rock Fortress",
            "Sunset at the water gardens",
          ],
        },
        {
          day: "Day 3",
          stopCode: "DAM",
          title: "Caves & Village Life",
          activities: [
            "Dambulla Cave Temple",
            "Village trek by ox-cart and catamaran near Habarana",
            "Afternoon safari at Minneriya National Park",
          ],
        },
        {
          day: "Day 4",
          stopCode: "KDY",
          title: "The Kandyan Kingdom",
          activities: [
            "Transfer to Kandy via the Matale spice route",
            "Temple of the Sacred Tooth Relic at dusk",
            "Kandyan dance performance",
          ],
        },
        {
          day: "Day 5",
          stopCode: "NUE",
          title: "Into the Tea Highlands",
          activities: [
            "Royal Botanical Gardens, Peradeniya",
            "Scenic drive to Nuwara Eliya through tea country",
            "Working tea factory tour",
            "Excursion to Horton Plains for World's End and Baker's Falls",
          ],
        },
        {
          day: "Day 6",
          stopCode: "ELL",
          title: "Ella by Rail",
          activities: [
            "The Nanu Oya–Ella highland train",
            "Sunrise hike to Little Adam's Peak",
            "Nine Arch Bridge at golden hour",
          ],
        },
        {
          day: "Day 7",
          stopCode: "YAL",
          title: "Wild Sri Lanka",
          activities: [
            "Transfer to Yala National Park",
            "Dawn leopard and elephant safari",
            "Evening at a beachside eco-lodge",
          ],
        },
        {
          day: "Day 8",
          stopCode: "BEN",
          title: "The Bentota Finale",
          activities: [
            "Transfer to the west coast at Bentota",
            "Boat safari up the Madu River, past cinnamon islands and a mangrove sanctuary",
            "Afternoon Ayurvedic treatment, or a river safari, windsurfing or jet-skiing instead",
            "Farewell dinner on the beach",
          ],
        },
        {
          day: "Day 9",
          stopCode: "CMB",
          title: "Departure",
          activities: [
            "Leisurely morning on the coast",
            "Transfer to Bandaranaike International Airport",
          ],
        },
      ],
      departureTime: "Flexible",
      returnTime: "Flexible",
      included: [
        "Private air-conditioned vehicle throughout",
        "English-speaking guide",
        "8 nights hand-picked hotels",
        "Daily breakfast",
        "Yala safari jeep",
        "Highland train tickets",
        "Madu River boat safari",
      ],
      excluded: [
        "International flights",
        "Entrance fees",
        "Lunch & dinner (except Day 1)",
        "Ayurvedic treatments at Bentota",
        "Travel insurance",
        "Personal expenses",
      ],
    },
    {
      id: "ancient-kingdoms-elephant-trail",
      title: "Ancient Kingdoms & Elephants",
      tagline: "Two lost capitals, and sixty elephants at bath time",
      location: "Anuradhapura",
      region: "Cultural Triangle",
      province: "North Central Province",
      price: 685,
      priceNote: "private guide & vehicle included",
      rating: 4.8,
      reviews: 143,
      duration: "4 Days",
      durationDays: 4,
      persons: "2 Persons",
      difficulty: "Easy",
      bestSeason: "Year-round",
      destinations: "4 Destinations",
      image: "./tour-pinnawela-1.jpg",
      gallery: [
        "./tour-pinnawela-1.jpg",
        "./tour-anuradhapura-1.jpg",
        "./tour-polonnaruwa-1.jpg",
      ],
      description:
        "North from Colombo, past the last of the coconut country, lies a herd of some sixty elephants at Pinnawela and, beyond them, two capitals the jungle spent centuries reclaiming - Anuradhapura's Ruwanwelisaya dagoba, one of the largest brick monuments on earth, and Polonnaruwa's Gal Vihara, four Buddha figures cut from a single sheet of granite. A quieter, deeper cut through the Cultural Triangle than the Sigiriya day-trip crowds ever see.",
      highlights: [
        "Bathing and bottle-feeding hour at the Pinnawela Elephant Orphanage",
        "Ruwanwelisaya Stupa and the sacred Jaya Sri Maha Bodhi in Anuradhapura",
        "Gal Vihara's rock-cut Buddha statues at Polonnaruwa",
        "Dambulla's five painted cave shrines on the road home",
      ],
      route: ["CMB", "PIN", "ANU", "POL", "DAM", "CMB"],
      stops: [
        STOPS["CMB"],
        STOPS["PIN"],
        STOPS["ANU"],
        STOPS["POL"],
        STOPS["DAM"],
      ],
      itinerary: [
        {
          day: "Day 1",
          stopCode: "PIN",
          title: "Elephants at Pinnawela",
          activities: [
            "Morning pickup from Bandaranaike International Airport, transfer north",
            "Arrive at the Pinnawela Elephant Orphanage for the riverside bathing hour",
            "Watch the bottle-feeding of the herd's youngest orphans",
            "Continue toward Anuradhapura and check in for the night",
          ],
        },
        {
          day: "Day 2",
          stopCode: "ANU",
          title: "The First Capital",
          activities: [
            "Full day exploring Sri Lanka's first ancient capital",
            "Ruwanwelisaya Stupa and the Jaya Sri Maha Bodhi, grown from a cutting of the tree Buddha sat beneath",
            "Isurumuniya Rock Temple and the Royal Twin Ponds",
            "Mihintale - the mountain pilgrimage site where Buddhism first reached the island in the 3rd century BC",
            "Overnight in Anuradhapura",
          ],
        },
        {
          day: "Day 3",
          stopCode: "POL",
          title: "The Second Capital",
          activities: [
            "Transfer toward Polonnaruwa via Habarana, the small lakeside town at the Cultural Triangle's centre",
            "Continue to Polonnaruwa, Sri Lanka's 11th-century royal seat",
            "Gal Vihara's four Buddha statues, carved from a single granite face",
            "The Royal Palace ruins and the Parakrama Samudra reservoir",
            "Overnight near Dambulla",
          ],
        },
        {
          day: "Day 4",
          stopCode: "DAM",
          title: "Cave Temples & the Road Home",
          activities: [
            "Morning visit to the Dambulla Cave Temple's painted shrines",
            "Photo stop at the base of Sigiriya Rock en route",
            "Afternoon transfer back to Bandaranaike International Airport for departure",
          ],
        },
      ],
      departureTime: "06:30 AM",
      returnTime: "06:00 PM",
      included: [
        "Private air-conditioned vehicle",
        "English-speaking guide",
        "3 nights accommodation",
        "Daily breakfast",
        "Pinnawela Elephant Orphanage entry",
      ],
      excluded: [
        "Entrance fees",
        "Lunch & dinner",
        "Travel insurance",
        "Personal expenses",
        "Optional elephant ride",
      ],
    },
    {
      id: "beach-holiday-getaway",
      title: "Beach Holiday Getaway",
      tagline: "Sun, surf, and the largest animal that has ever lived",
      location: "South Coast",
      region: "South Coast",
      province: "Southern Province",
      price: 650,
      priceNote: "private guide & vehicle included",
      rating: 4.8,
      reviews: 121,
      duration: "4 Days",
      durationDays: 4,
      persons: "2 Persons",
      difficulty: "Easy",
      bestSeason: "Year-round",
      destinations: "4 Destinations",
      image: "./tour-mirissa-whale-1.jpg",
      gallery: [
        "./tour-mirissa-whale-1.jpg",
        "./tour-hikkaduwa-1.jpg",
        "./tour-maduganga-1.jpg",
        "./tour-bentota-1.jpg",
      ],
      description:
        "Sri Lanka's beaches run 1,340km around the island, and this route strings together its best stretch of coast - Negombo's fishing lagoon, Hikkaduwa's coral sanctuary, a dawn run out to blue whales at Mirissa, and a Madu River boat safari through Bentota's mangroves to close it out.",
      highlights: [
        "Dawn boat excursion for blue whales, sperm whales, and spinner dolphins off Mirissa",
        "Snorkeling over Hikkaduwa's coral sanctuary",
        "A boat safari up the Madu River, past cinnamon islands and a mangrove sanctuary",
        "A turtle hatchery and an optional Ayurvedic treatment at Bentota",
        "Negombo's Dutch canal and fish market on arrival",
      ],
      route: ["CMB", "NEG", "HIK", "MIR", "BEN", "CMB"],
      stops: [
        STOPS["CMB"],
        STOPS["NEG"],
        STOPS["HIK"],
        STOPS["MIR"],
        STOPS["BEN"],
      ],
      itinerary: [
        {
          day: "Day 1",
          stopCode: "NEG",
          title: "Arrival on the Coast",
          activities: [
            "Airport pickup and short transfer to Negombo",
            "Wander the Dutch canal and the morning fish market",
            "Free evening at your beachfront hotel",
          ],
        },
        {
          day: "Day 2",
          stopCode: "HIK",
          title: "Reef Day",
          activities: [
            "Morning transfer south to Hikkaduwa",
            "Snorkeling over the coral sanctuary's reef fish and resident turtles",
            "Optional surf lesson on Hikkaduwa's beach break",
            "Continue to Mirissa for the night",
          ],
        },
        {
          day: "Day 3",
          stopCode: "MIR",
          title: "Whales at Dawn",
          activities: [
            "Early departure for a whale and dolphin watching boat excursion",
            "Search waters known for blue whales, sperm whales, and spinner dolphin pods",
            "Afternoon at Mirissa beach and Coconut Tree Hill",
            "Transfer to Bentota for the night",
          ],
        },
        {
          day: "Day 4",
          stopCode: "BEN",
          title: "Bentota & Departure",
          activities: [
            "Boat safari up the Madu River, past cinnamon islands and a mangrove sanctuary",
            "Visit a turtle hatchery on the riverbank",
            "Optional Ayurvedic treatment before you go",
            "Transfer back to Bandaranaike International Airport for departure",
          ],
        },
      ],
      departureTime: "06:00 AM",
      returnTime: "07:00 PM",
      included: [
        "Private air-conditioned vehicle",
        "English-speaking guide",
        "3 nights beachfront hotel",
        "Daily breakfast",
        "Whale-watching boat ticket",
        "Madu River boat safari",
      ],
      excluded: [
        "Entrance fees",
        "Snorkeling gear rental",
        "Ayurvedic treatments",
        "Lunch & dinner",
        "Travel insurance",
        "Personal expenses",
      ],
    },
    {
      id: "ramayana-trail",
      title: "The Ramayana Trail",
      tagline: "Sri Lanka's chapter in the epic of Rama and Ravana",
      location: "Island-wide",
      region: "Full Circuit",
      province: "Multi-Province",
      price: 995,
      priceNote: "private guide & vehicle included",
      rating: 4.7,
      reviews: 58,
      duration: "6 Days",
      durationDays: 6,
      persons: "2 Persons",
      difficulty: "Easy",
      bestSeason: "Year-round",
      destinations: "6 Destinations",
      image: "./tour-ravana-falls-1.jpg",
      gallery: ["./tour-ravana-falls-1.jpg"],
      description:
        "A different route across the same island, tracing the sites Sri Lankan tradition ties to the Ramayana - the temple where Rama sought release from a warrior's curse, the hillside where Hanuman searched for Sita, the stream where she is said to have proven her innocence by fire, and the falls named for the demon-king Ravana himself. Six days of Hindu shrines and hill-country legend, for travelers who want the island's mythology alongside its scenery.",
      highlights: [
        "Munneshwaram and Manavari temples, where Rama is said to have installed the first lingam",
        "The Sri Bhakta Hanuman Temple at Ramboda",
        "Seetha Amman Temple and the Divurumpola oath-taking site",
        "Ravana Ella Falls and the caves above it",
        "Kataragama, sacred to Buddhists, Hindus, and Muslims alike",
      ],
      route: ["CMB", "NEG", "KDY", "NUE", "BAN", "ELL", "KAT", "CMB"],
      stops: [
        STOPS["CMB"],
        STOPS["NEG"],
        STOPS["KDY"],
        STOPS["NUE"],
        STOPS["BAN"],
        STOPS["ELL"],
        STOPS["KAT"],
      ],
      itinerary: [
        {
          day: "Day 1",
          stopCode: "NEG",
          title: "Arrival in Negombo",
          activities: [
            "Airport pickup and transfer to your Negombo hotel",
            "Evening at leisure on the beach",
          ],
        },
        {
          day: "Day 2",
          stopCode: "KDY",
          title: "Temples of the Curse",
          activities: [
            "Munneshwaram Temple in Chilaw, where Rama sought to lift a Brahmin's curse",
            "Manavari Temple, said to hold the first lingam Rama installed",
            "Continue to the hill country, overnight near Kandy",
          ],
        },
        {
          day: "Day 3",
          stopCode: "NUE",
          title: "Hanuman's Search",
          activities: [
            "Sri Bhakta Hanuman Temple at Ramboda, on the hills where Hanuman searched for Sita",
            "Transfer to Nuwara Eliya",
            "Evening visit to the Gayathri Peedam shrine",
          ],
        },
        {
          day: "Day 4",
          stopCode: "BAN",
          title: "Sita's Trial",
          activities: [
            "Seetha Amman Temple at Seetha Eliya, beside the stream where Sita is said to have bathed",
            "Divurumpola, where she proved her innocence by fire",
            "Overnight in Bandarawela",
          ],
        },
        {
          day: "Day 5",
          stopCode: "KAT",
          title: "Ravana's Falls & Kataragama",
          activities: [
            "Ravana Ella Falls and the cave network above it, tied to King Ravana in local legend",
            "Continue south to the multi-faith shrine complex of Kataragama",
            "Overnight in Kataragama",
          ],
        },
        {
          day: "Day 6",
          stopCode: "CMB",
          title: "Departure",
          activities: [
            "Transfer toward Colombo, visiting the Panchamuga Anjaneyar Temple - the only Hanuman shrine of its kind",
            "Transfer to Bandaranaike International Airport",
          ],
        },
      ],
      departureTime: "Flexible",
      returnTime: "Flexible",
      included: [
        "Private air-conditioned vehicle",
        "English-speaking guide",
        "5 nights accommodation",
        "Daily breakfast",
      ],
      excluded: [
        "International flights",
        "Entrance fees",
        "Lunch & dinner",
        "Travel insurance",
        "Personal expenses",
      ],
    },
  ]);

  getTours() {
    return this.tours;
  }

  getTourById(id: string) {
    return this.tours().find((t) => t.id === id);
  }
}
